<script setup>
import { computed, ref, watch } from "vue";
import { Message, MessageBox } from "element-ui";
import { useGetters, useActions } from "vuex-composition-helpers";
import DateTimeHelper from "@/common/DateTimeHelper";
import AtsLogo from "@/components/AtsLogo/AtsLogo";
import ProfilesApi from "@/common/http/ProfilesApi";

const {
  currentCandidate,
} = useGetters([
  "currentCandidate",
]);

const {
  reloadProfileApplications,
} = useActions([
  "reloadProfileApplications",
]);

const applications = computed(() => {
  return currentCandidate.value.applications || [];
});

// Available stage/reject actions per atsJobId, fetched from the backend.
// Empty for a job means the current user has no allowed actions for it.
const stageActionsByJob = ref({});
const applyingJobIds = ref([]);
const appliedActionNamesByJob = ref({});

// Bumped on every fetch so a late/overlapping run (the candidate load fires several
// mergeCurrentCandidate updates) can be ignored instead of wiping fresh results.
let fetchToken = 0;

watch(applications, fetchStageActions, { immediate: true });

function fetchStageActions() {
  const profileId = currentCandidate.value.profileId;
  const token = ++fetchToken;

  // Clear immediately so a previous candidate's actions can't be applied to the new one
  // while this fetch is in flight (both candidates may share an atsJobId).
  stageActionsByJob.value = {};
  appliedActionNamesByJob.value = {};

  if (!profileId) {
    return;
  }

  // One batched request returns actions for all of the profile's non-archived applications,
  // keyed by atsJobId. Avoids a request per job (some candidates have dozens).
  ProfilesApi.atsReviewerStageActionsForProfile({ profileId }).then(({ data }) => {
    if (token !== fetchToken || currentCandidate.value.profileId !== profileId) {
      return;
    }

    stageActionsByJob.value = data.actionsByJob || {};
    appliedActionNamesByJob.value = {};
  }).catch(() => {});
}

// Actions per job, sorted once (advancements first, rejections last). A computed so the up-to-~20
// items don't each re-sort a fresh array on every render (the template calls actionsForJob per row
// and isFirstRejection per item).
const sortedActionsByJob = computed(() => {
  const result = {};
  for (const [atsJobId, actions] of Object.entries(stageActionsByJob.value)) {
    result[atsJobId] = [...actions].sort((a, b) => Number(a.isRejection) - Number(b.isRejection));
  }
  return result;
});

function actionsForJob(application) {
  return sortedActionsByJob.value[application.atsJobId] || [];
}

function isFirstRejection(application, action) {
  return action.isRejection && actionsForJob(application).find(a => a.isRejection) === action;
}

function selectAction(application, action) {
  if (action.isRejection) {
    MessageBox.confirm(
      `Reject this application with "${action.name}"?`,
      "Confirm rejection",
      { confirmButtonText: "Reject", cancelButtonText: "Cancel", type: "warning" }
    ).then(() => applyAction(application, action)).catch(() => {});
  } else {
    applyAction(application, action);
  }
}

async function applyAction(application, action) {
  // Send the exact application the row shows; the backend validates the ref against a
  // team+profile+job-scoped event before using it (so it can't be spoofed or ambiguous). Capture the
  // profile so a candidate switch mid-request can be detected.
  const profileId = currentCandidate.value.profileId;
  applyingJobIds.value = [...applyingJobIds.value, application.atsJobId];

  try {
    await ProfilesApi.applyAtsReviewerStageAction({
      profileId,
      atsJobId: application.atsJobId,
      applicationAtsRef: application.applicationAtsRef,
      actionType: action.actionType,
      actionId: action.id,
    });

    // The candidate may have changed while the request was in flight; don't paint one candidate's
    // result onto another. The push already happened server-side regardless.
    if (currentCandidate.value.profileId !== profileId) {
      return;
    }

    // Show the stage the candidate moves to (e.g. "Onsite" / "Rejected"), not the action's label
    // (e.g. "Advance to Onsite"). Falls back to the label for any action without a target stage.
    appliedActionNamesByJob.value = { ...appliedActionNamesByJob.value, [application.atsJobId]: action.targetStageName || action.name };
    stageActionsByJob.value = Object.fromEntries(
      Object.entries(stageActionsByJob.value).filter(([atsJobId]) => String(atsJobId) !== String(application.atsJobId))
    );
    Message({ type: "success", message: "Update sent to the ATS. The new stage may take a few minutes to appear." });

    // Refetch so the server-side pending override backs the badge (survives reload until the ATS syncs).
    reloadProfileApplications();
  } catch (error) {
    if (currentCandidate.value.profileId === profileId) {
      Message({ type: "error", message: "Failed to update the stage. Please try again." });
    }
  } finally {
    applyingJobIds.value = applyingJobIds.value.filter(id => id !== application.atsJobId);
  }
}

function displayStage(application) {
  return appliedActionNamesByJob.value[application.atsJobId] || application.currentStage;
}

function getStageType(archiveReason) {
  if (archiveReason === "HIRED") {
    return "positive";
  }

  const negativeReasons = ["WITHDREW", "REJECTED", "NOT_HIRED"];
  if (negativeReasons.includes(archiveReason)) {
    return "negative";
  }

  return "neutral";
}
</script>

<template>
  <div id="tab-applications">
    <div v-if="applications.length > 0" class="applications-container">
      <ul>
        <li v-for="application in applications" :key="application.atsJobId" class="application-item">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="project-icon-wrapper mr-8">
                <AtsLogo
                  v-if="application.atsJobId"
                  :ats-name="application.atsJobAtsName"
                  :problem="application.atsJobProblem"
                />
                <img v-else class="project-icon" :class="{ 'is-link': application.projectId }" :src="require('@/assets/images/icons/briefcase.svg')" />
              </div>
              <div>
                <div class="d-flex align-items-center">
                  <router-link
                    v-if="application.projectId"
                    :to="{ name: 'job-root', params: { projectId: application.projectId } }"
                    class="application-link"
                  >
                    {{ application.jobName || application.projectName }} <span v-if="application.reqId" class="req-id">({{ application.reqId }})</span>
                  </router-link>
                  <span v-else class="application-job-name">
                    {{ application.jobName || application.projectName }} <span v-if="application.reqId" class="req-id">({{ application.reqId }})</span>
                  </span>
                </div>
                <div v-if="application.appliedDate || application.archiveDescription" class="application-meta">
                  <el-tooltip v-if="application.appliedDate" :content="DateTimeHelper.exactDateTimeInTimezone(application.appliedDate)">
                    <span class="applied-date cursor-help">
                      Applied {{ DateTimeHelper.relativeTimeFromNow(application.appliedDate) }}
                    </span>
                  </el-tooltip>

                  <template v-if="application.archiveDescription">
                    <el-divider v-if="application.appliedDate" direction="vertical" class="meta-divider" />
                    <span class="archive-description">
                      {{ application.archiveDescription }}

                      <el-tooltip
                        v-if="application.lastActivityAt"
                        :content="DateTimeHelper.exactDateTimeInTimezone(application.lastActivityAt)"
                      >
                        <span class="cursor-help">
                          ({{ DateTimeHelper.relativeTimeFromNow(application.lastActivityAt) }})
                        </span>
                      </el-tooltip>
                    </span>
                  </template>
                </div>
              </div>
            </div>
            <div v-if="displayStage(application) || application.projectStatus || actionsForJob(application).length" class="application-status-badges">
              <el-dropdown
                v-if="actionsForJob(application).length"
                trigger="click"
                class="stage-actions-dropdown"
                @command="action => selectAction(application, action)"
              >
                <el-button size="mini" :loading="applyingJobIds.includes(application.atsJobId)">
                  Update stage<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-for="action in actionsForJob(application)"
                    :key="`${action.actionType}-${action.id}`"
                    :command="action"
                    :divided="isFirstRejection(application, action)"
                    :class="{ 'rejection-action': action.isRejection }"
                  >
                    {{ action.name }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>

              <span v-if="displayStage(application)" class="badge badge-stage" :class="`badge-stage-${getStageType(application.archiveReason)}`">
                {{ displayStage(application) }}
              </span>
              <span v-else-if="application.projectStatus" class="badge badge-status">
                {{ application.projectStatus }}
              </span>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div v-else class="no-applications">
      This candidate has not applied to any jobs
    </div>
  </div>
</template>

<style scoped lang="scss">
#tab-applications {
  width: 100%;
  font-size: 14px;
  color: $tf-font-default;
  cursor: default;

  h4 {
    font-size: 20px;
    margin-bottom: 16px;
  }

  .applications-container {
    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;

      .application-item {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid $black-10;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .project-icon-wrapper {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          align-self: flex-start;
          margin-top: 2px;

          .project-icon {
            width: 100%;
            height: 100%;
            object-fit: contain;

            &.is-link {
              filter: $filter-blue-100;
            }
          }
        }

        .application-link {
          color: $blue-100;
          font-weight: 500;
          font-size: 14px;

          &:hover {
            text-decoration: none;
          }
        }

        .application-job-name {
          color: $tf-font-default;
          font-weight: 500;
          font-size: 14px;
        }

        .application-meta {
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: $tf-font-ghost;

          .meta-divider {
            margin: 0;
            height: 12px;
            background-color: $black-50;
          }

          .archive-description {
            font-weight: 500;
            color: $tf-font-default;
          }

          .applied-date {
            color: $tf-font-ghost;
          }
        }

        .req-id {
          color: $tf-font-ghost;
          font-weight: 400;
          font-size: 12px;
        }

        .application-status-badges {
          display: flex;
          gap: 8px;
          align-items: center;

          .badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            white-space: nowrap;
          }

          .badge-stage {
            background-color: $black-10;
            color: $tf-font-default;

            &.badge-stage-positive {
              background-color: $green-10;
              color: $green-100;
            }

            &.badge-stage-negative {
              background-color: $magenta-10;
              color: $magenta-100;
            }

            &.badge-stage-neutral {
              background-color: $black-10;
              color: $tf-font-default;
            }
          }

          .badge-status {
            background-color: $green-10;
            color: $green-100;
          }
        }
      }
    }
  }

  .no-applications {
    color: $tf-font-ghost;
    font-size: 14px;
    margin-top: 8px;
  }
}
</style>

<style lang="scss">
// Not scoped: el-dropdown-menu is appended to body
.el-dropdown-menu__item.rejection-action {
  color: $magenta-100;
}
</style>
